import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AddStockItemModal, type CreatedStockItem } from '@/components/store/AddStockItemModal'
import { useCurrency } from '@/contexts/CurrencyContext'
import { STOCK_ITEM_OPTIONS_QUERY_KEY, stockItemRefs, useStockItemOptions } from '@/hooks/useStockItemOptions'
import { currencyInputPrefix } from '@/lib/formatMoney'
import {
  buildRecipePayload,
  collectBlocks,
  defaultUnitFor,
  fromServerComponents,
  newLine,
  packagingOnly,
  recipeTotals,
  type RecipeLineDraft,
  type StockItemRef,
  type VariantDraft,
} from '@/lib/recipeEditor'
import {
  changedVariantIds,
  draftSnapshot,
  initialDraft,
  neighbours,
  onServerChange,
  optionPrice,
  optionSaveWrites,
  partialSaveMessage,
  planSave,
  saveBlockReason,
  serverSignature,
  sharedChanges,
  type CostingSource,
  type SaveStep,
  type SharedChange,
  type SheetDraft,
} from '@/lib/costingSheetState'
import { COST_MODE_LABELS, type CostMode } from '@/lib/productFormPayload'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import type { Product, ProductRecipe } from '@/types'
import { RecipeEditor } from './RecipeEditor'
import { RecipeMethodSection, type RecipeMethodGuard } from '@/components/recipe-card/RecipeMethodSection'
import { RC } from '@/lib/recipeCard/recipeCardStrings'
import { VariantsSection } from './VariantsSection'
import { PRODUCT_RECIPE_QK, invalidateAfterRecipeSave, saveIngredientCost } from './costingQueries'

export { PRODUCT_RECIPE_QK }

const MODES: CostMode[] = ['recipe', 'amount', 'percent', 'none']
const MODE_HINT: Record<CostMode, string> = {
  recipe: 'Stock is deducted on every sale and restored on void or refund. Reports use the ingredient cost.',
  amount: 'An assumed cost per serving, separate from the expenses ledger. Reports show it as projected margin.',
  percent: 'The cost follows the price: change the price and it re-figures itself.',
  none: 'Reports will show this item as having no cost.',
}

export interface CopyCandidate {
  id: string
  name: string
  count: number
}

type CreateTarget = { where: 'base' | { optionId: string }; rowKey: string | null; name: string }

/**
 * The item's own costing page (F-FE-N157): pick HOW it is costed, then edit
 * that in place — the recipe editor, the variants, or a manual figure — and
 * walk to the next item without closing. Opens from any row on the Costing &
 * Recipes tab and deep-links via ?tab=costing&item=<id>.
 *
 * Hardened after audit/RECIPE_BUILDER_AUDIT_2026-09-14.md: the draft is never
 * replaced while the owner is editing (U-2), leaving with unsaved edits asks
 * first (U-3), a manually costed item opens clean (U-5), shared option recipes
 * are confirmed before they change (U-1), and Save always says why it is grey
 * (U-15). The rules live in lib/costingSheetState.ts.
 */
export function CostingSheet({
  product,
  walkIds,
  copyCandidates,
  onNavigate,
  onClose,
}: {
  product: Product
  /** The list order frozen when the sheet opened, for Prev / Next. */
  walkIds: string[]
  /** Every item that has a recipe, for "Start from another item's recipe". */
  copyCandidates: CopyCandidate[]
  onNavigate: (id: string) => void
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { currencyCode, formatCurrency } = useCurrency()
  const { data: stockOptions } = useStockItemOptions()
  // Items created from this sheet, shown until the options query catches up.
  const [extraItems, setExtraItems] = useState<Record<string, StockItemRef>>({})
  const items = useMemo(() => ({ ...extraItems, ...stockItemRefs(stockOptions) }), [extraItems, stockOptions])
  const itemList = useMemo(() => Object.values(items).sort((a, b) => a.name.localeCompare(b.name)), [items])

  const recipeQuery = useQuery({
    queryKey: [PRODUCT_RECIPE_QK, product.id],
    queryFn: () => apiClient.getProductRecipe(product.id).then((r) => r.data as ProductRecipe),
  })
  // Only ever the recipe of THIS product — never the previous one's while the next loads.
  const recipe = recipeQuery.data && String(recipeQuery.data.product_id) === String(product.id) ? recipeQuery.data : undefined

  const source: CostingSource | null = useMemo(
    () =>
      recipe
        ? {
            recipe,
            projected_cost: product.projected_cost,
            projected_cost_basis: product.projected_cost_basis,
            projected_cost_percent: product.projected_cost_percent,
          }
        : null,
    [recipe, product.projected_cost, product.projected_cost_basis, product.projected_cost_percent],
  )
  const signature = source ? serverSignature(source) : null

  // ── Draft ───────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<CostMode>('none')
  const [lines, setLines] = useState<RecipeLineDraft[]>([])
  const [yieldStr, setYieldStr] = useState('1')
  const [amount, setAmount] = useState('')
  const [percent, setPercent] = useState('')
  const [variantDrafts, setVariantDrafts] = useState<Record<string, VariantDraft>>({})
  const [baseline, setBaseline] = useState('')
  const [loadedForId, setLoadedForId] = useState<string | null>(null)
  const [appliedSig, setAppliedSig] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [saving, setSaving] = useState(false)
  const [createTarget, setCreateTarget] = useState<CreateTarget | null>(null)
  const [leave, setLeave] = useState<{ run: () => void } | null>(null)
  const [sharedConfirm, setSharedConfirm] = useState<{ list: SharedChange[]; after?: () => void } | null>(null)
  const [startFrom, setStartFrom] = useState('')
  const expecting = useRef<'adopt' | 'rebase' | null>(null)
  const modeRefs = useRef<Array<HTMLButtonElement | null>>([])

  const ready = Boolean(recipe) && loadedForId === product.id
  const changedIds = useMemo(() => (recipe ? changedVariantIds(variantDrafts, recipe.variants) : []), [recipe, variantDrafts])
  const liveSnapshot = draftSnapshot({ mode, lines, yieldStr, amount, percent })
  const dirty = ready && (liveSnapshot !== baseline || (mode === 'recipe' && changedIds.length > 0))
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  const applyDraft = (d: SheetDraft, sig: string) => {
    setMode(d.mode)
    setLines(d.lines)
    setYieldStr(d.yieldStr)
    setAmount(d.amount)
    setPercent(d.percent)
    setVariantDrafts(d.variants)
    setBaseline(draftSnapshot(d))
    setLoadedForId(product.id)
    setAppliedSig(sig)
    setStale(false)
    setStartFrom('')
  }

  useEffect(() => {
    if (!source || !signature) return
    const action = onServerChange({
      loadedForId,
      productId: product.id,
      appliedSignature: loadedForId === product.id ? appliedSig : null,
      signature,
      dirty: dirtyRef.current,
      expecting: loadedForId === product.id ? expecting.current : null,
    })
    if (action === 'ignore') return
    expecting.current = null
    if (action === 'adopt') {
      applyDraft(initialDraft(source), signature)
    } else if (action === 'rebase') {
      // Our own change ("Remove it", "Set cost", a partial save): move the
      // baseline to the server, keep everything being typed.
      setBaseline(draftSnapshot(initialDraft(source)))
      setAppliedSig(signature)
    } else {
      setStale(true)
      setAppliedSig(signature)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, product.id])

  // ── Derived figures ─────────────────────────────────────────────────────
  const yieldN = Number(yieldStr) > 0 ? Number(yieldStr) : 1
  const price = Number(product.price) || 0
  const totals = useMemo(() => recipeTotals(lines, items, yieldN, price), [lines, items, yieldN, price])
  const takeawayTotals = useMemo(() => recipeTotals(lines, items, yieldN, price, 'takeaway'), [lines, items, yieldN, price])
  const baseBlocks = useMemo(() => collectBlocks(lines, items, price, yieldN, formatCurrency, 'product'), [lines, items, price, yieldN, formatCurrency])
  const variantBlockCount = useMemo(() => {
    if (!recipe) return 0
    let n = 0
    for (const [id, d] of Object.entries(variantDrafts)) {
      const v = recipe.variants.find((x) => x.option_id === id)
      if (!v) continue
      n += collectBlocks(d.lines, items, optionPrice(v, price), 1, formatCurrency, 'option').length
    }
    return n
  }, [recipe, variantDrafts, items, price, formatCurrency])
  const basePayload = useMemo(() => buildRecipePayload(lines, yieldN, 'product'), [lines, yieldN])
  const serverBaseRows = recipe?.components.length ?? 0

  const hasEstimate = product.projected_cost != null || product.projected_cost_basis === 'percent'
  const estimateValue =
    product.projected_cost != null
      ? Number(product.projected_cost)
      : product.projected_cost_basis === 'percent'
        ? (price * Number(product.projected_cost_percent ?? 0)) / 100
        : null
  // F-MM-N13: checkout only prices from a recipe when EVERY ingredient has a
  // cost; until then it books the owner's own estimate (audit U-9).
  const estimateInUse = mode === 'recipe' && totals.uncosted > 0 && hasEstimate

  const manualCost = mode === 'amount' ? Number(amount) : mode === 'percent' ? (price * Number(percent)) / 100 : null
  const manualTyped = mode === 'amount' ? amount.trim() !== '' : mode === 'percent' ? percent.trim() !== '' : false
  const headline =
    mode === 'recipe' ? (totals.uncosted > 0 ? null : totals.cost) : manualTyped && manualCost != null && Number.isFinite(manualCost) ? manualCost : null
  const profit = headline != null ? price - headline : null
  const marginPct = headline != null && price > 0 ? ((price - headline) / price) * 100 : null
  const loss = headline != null && price > 0 && headline > price

  const { prevId, nextId } = neighbours(walkIds, product.id)

  const reason = saveBlockReason({
    pending: saving,
    dirty,
    mode,
    blockCount: baseBlocks.length + variantBlockCount,
    basePayloadCount: basePayload.length,
    serverBaseRows,
    changedOptionCount: changedIds.length,
    yieldStr,
    variantMultipliers: changedIds.map((id) => variantDrafts[id]?.multiplier).filter((m): m is string => m != null),
    amount,
    percent,
  })
  const canSave = ready && reason === null
  const removesRecipe = mode !== 'recipe' && serverBaseRows > 0

  // ── Saving ──────────────────────────────────────────────────────────────
  const optionLabel = (id: string) => recipe?.variants.find((v) => v.option_id === id)?.option_name ?? 'option'

  const runStep = async (step: SaveStep) => {
    switch (step.kind) {
      case 'base':
        await apiClient.putProductRecipe(product.id, { recipe_yield: yieldN, components: basePayload })
        return
      case 'option': {
        const savedOwn = Boolean(recipe?.variants.find((v) => v.option_id === step.optionId)?.own_recipe)
        for (const w of optionSaveWrites(variantDrafts[step.optionId], savedOwn)) {
          await apiClient.putOptionRecipe(step.optionId, w.body, w.productScoped ? product.id : undefined)
        }
        return
      }
      case 'manual': {
        const trio =
          mode === 'amount'
            ? { projected_cost_basis: 'amount' as const, projected_cost: Number(amount), projected_cost_percent: null }
            : mode === 'percent'
              ? { projected_cost_basis: 'percent' as const, projected_cost: null, projected_cost_percent: Number(percent) }
              : { projected_cost_basis: 'amount' as const, projected_cost: null, projected_cost_percent: null }
        await apiClient.updateProduct(product.id, trio)
        return
      }
      case 'removeRecipe':
        await apiClient.putProductRecipe(product.id, { recipe_yield: 1, components: [] })
        return
    }
  }

  const runSave = async (): Promise<boolean> => {
    const steps = planSave({
      mode,
      basePayloadCount: basePayload.length,
      serverBaseRows,
      changedOptions: mode === 'recipe' ? changedIds.map((id) => ({ id, label: optionLabel(id) })) : [],
    })
    const done: SaveStep[] = []
    setSaving(true)
    try {
      for (const step of steps) {
        await runStep(step)
        done.push(step)
      }
      expecting.current = 'adopt'
      toastHelpers.success('Saved', `${product.name} costing updated`)
      return true
    } catch (e) {
      const failed = steps[done.length]
      const why = e instanceof Error ? e.message : String(e)
      if (done.length > 0 && failed) {
        // Some writes landed: keep the owner's draft and move the baseline, so
        // what didn't save still shows as unsaved.
        expecting.current = 'rebase'
        toastHelpers.error('Partly saved', partialSaveMessage(done, failed, why))
      } else {
        toastHelpers.apiError('Save costing', e)
      }
      return false
    } finally {
      setSaving(false)
      invalidateAfterRecipeSave(qc, product.id)
    }
  }

  const startSave = (after?: () => void) => {
    if (!canSave) return
    const shared = mode === 'recipe' && recipe ? sharedChanges(changedIds, recipe.variants, variantDrafts) : []
    if (shared.length > 0) {
      setSharedConfirm({ list: shared, after })
      return
    }
    void runSave().then((ok) => {
      if (ok && after) after()
    })
  }

  const leaveCosting = (run: () => void) => {
    if (dirty && !saving) setLeave({ run })
    else run()
  }
  // The kitchen recipe card below has its own draft and its own save (kitchen
  // flow). Unsaved steps are asked about first, then the sheet's own edits.
  // Without the kitchen flow the section is not mounted, the guard stays null
  // and leaving is exactly what it was.
  const methodGuard = useRef<RecipeMethodGuard | null>(null)
  const [methodLeave, setMethodLeave] = useState<{ run: () => void; canSave: boolean } | null>(null)
  const requestLeave = (run: () => void) => {
    const g = methodGuard.current
    if (g?.dirty && !saving) setMethodLeave({ run: () => leaveCosting(run), canSave: g.canSave })
    else leaveCosting(run)
  }

  const removeDormant = async () => {
    try {
      await apiClient.updateProduct(product.id, { projected_cost_basis: 'amount', projected_cost: null, projected_cost_percent: null })
      expecting.current = 'rebase'
      toastHelpers.success('Removed', 'The manual estimate is gone; the recipe is the only cost now.')
    } catch (e) {
      toastHelpers.apiError('Remove estimate', e)
    } finally {
      invalidateAfterRecipeSave(qc, product.id)
    }
  }

  const saveCost = (item: StockItemRef, cost: number): Promise<boolean> =>
    saveIngredientCost(qc, item, cost, formatCurrency, () => {
      expecting.current = 'rebase'
    })

  const copyFrom = async (sourceId: string) => {
    try {
      const src = (await apiClient.getProductRecipe(sourceId)).data as ProductRecipe
      if (!src || src.components.length === 0) {
        toastHelpers.error('Nothing to copy', `${src?.name ?? 'That item'} has no recipe yet.`)
        return
      }
      setLines(fromServerComponents(src.components, src.recipe_yield))
      setYieldStr(String(src.recipe_yield || 1))
      setMode('recipe')
      toastHelpers.success('Copied', `${src.components.length} ingredients from ${src.name}. Adjust and save.`)
    } catch (e) {
      toastHelpers.apiError('Copy recipe', e)
    }
  }

  const onIngredientCreated = (created: CreatedStockItem) => {
    const target = createTarget
    const ref: StockItemRef = { id: created.id, name: created.name, unit: created.unit, unitCost: null }
    setExtraItems((prev) => ({ ...prev, [created.id]: ref }))
    qc.invalidateQueries({ queryKey: STOCK_ITEM_OPTIONS_QUERY_KEY })
    if (!target) return
    const place = (ls: RecipeLineDraft[]): RecipeLineDraft[] =>
      target.rowKey && ls.some((l) => l.key === target.rowKey)
        ? ls.map((l) => (l.key === target.rowKey ? { ...l, stock_item_id: created.id, unit: defaultUnitFor(created.unit) } : l))
        : [...ls, newLine(ref)]
    if (target.where === 'base') {
      setLines(place)
    } else {
      const optionId = target.where.optionId
      setVariantDrafts((prev) => {
        const d = prev[optionId]
        return d ? { ...prev, [optionId]: { ...d, lines: place(d.lines) } } : prev
      })
    }
  }

  const onModeKey = (e: React.KeyboardEvent) => {
    const i = MODES.indexOf(mode)
    let next = -1
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % MODES.length
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + MODES.length) % MODES.length
    if (next < 0) return
    e.preventDefault()
    setMode(MODES[next])
    modeRefs.current[next]?.focus()
  }

  const footerText = reason ?? (dirty ? 'Unsaved changes' : '')

  return (
    <Sheet open onOpenChange={(o) => { if (!o) requestLeave(onClose) }}>
      {/* Half the screen on a desktop (owner request 2026-09-14): the recipe
          grid, the variants and the live margin all need the width. Never
          narrower than 720px from md up, never wider than 1200px, and full
          width on a tablet or phone. */}
      <SheetContent size="lg" className="w-full p-0 sm:max-w-none md:w-[50vw] md:min-w-[720px] md:max-w-[1200px]" aria-describedby={undefined}>
        <SheetHeader className="border-b pr-12">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {product.category?.name ?? 'Item'} · sells for {formatCurrency(price)}
              </p>
              <SheetTitle className="truncate">{product.name}</SheetTitle>
              <SheetDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                {!ready ? (
                  <span>Loading…</span>
                ) : estimateInUse ? (
                  <span className="text-amber-800 dark:text-amber-300">
                    Checkout uses <span className="font-semibold tabular-nums">{formatCurrency(estimateValue ?? 0)}</span> (your estimate) until every ingredient has a cost
                    <span className="text-muted-foreground"> · ingredients so far {formatCurrency(totals.cost)}</span>
                  </span>
                ) : mode === 'recipe' && totals.uncosted > 0 ? (
                  <span className="text-amber-800 dark:text-amber-300">
                    Incomplete — {totals.uncosted} ingredient{totals.uncosted === 1 ? '' : 's'} without a cost
                    <span className="text-muted-foreground"> · so far {formatCurrency(totals.cost)}</span>
                  </span>
                ) : headline != null ? (
                  <>
                    <span className="tabular-nums">Cost <span className="font-semibold text-foreground">{formatCurrency(headline)}</span></span>
                    <span className="tabular-nums">Profit <span className={cn('font-semibold', loss ? 'text-destructive' : 'text-success-fg')}>{formatCurrency(profit ?? 0)}</span></span>
                    {marginPct != null && (
                      <Badge variant={loss ? 'danger-surface' : 'success-surface'}>{marginPct.toFixed(1)}% margin</Badge>
                    )}
                    {mode === 'recipe' && takeawayTotals.cost !== totals.cost && (
                      <span className="tabular-nums text-muted-foreground">takeaway {formatCurrency(takeawayTotals.cost)}</span>
                    )}
                  </>
                ) : (
                  <span>No cost set yet.</span>
                )}
              </SheetDescription>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={!prevId} onClick={() => prevId && requestLeave(() => onNavigate(prevId))} aria-label="Previous item">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={!nextId} onClick={() => nextId && requestLeave(() => onNavigate(nextId))} aria-label="Next item">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        <SheetBody className="space-y-5 overflow-y-auto">
          {!ready || !recipe ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {stale && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
                  <span className="flex-1">This item changed elsewhere. Your edits are still here; reload to see the saved version.</span>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => source && signature && applyDraft(initialDraft(source), signature)}>
                    Reload
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm" id="cost-source-label">How is the cost set?</Label>
                <div role="radiogroup" aria-labelledby="cost-source-label" onKeyDown={onModeKey} className="inline-flex max-w-full flex-wrap rounded-md border bg-background p-0.5">
                  {MODES.map((m, i) => (
                    <button
                      key={m}
                      ref={(el) => { modeRefs.current[i] = el }}
                      type="button"
                      role="radio"
                      aria-checked={mode === m}
                      tabIndex={mode === m ? 0 : -1}
                      onClick={() => setMode(m)}
                      className={cn('rounded px-3 py-1.5 text-xs font-medium transition-colors', mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                    >
                      {m === 'recipe' ? 'From ingredients' : COST_MODE_LABELS[m]}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{MODE_HINT[mode]}</p>
                {removesRecipe && (
                  <p className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
                    Saving removes this item&apos;s {serverBaseRows}-ingredient recipe, so its own ingredients stop being deducted when it sells.
                    Options with their own recipe (e.g. Iced) still deduct their ingredients.
                  </p>
                )}
              </div>

              {mode === 'recipe' && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Label htmlFor="recipe-yield" className="text-sm">This recipe makes</Label>
                    <Input
                      id="recipe-yield"
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      className={cn('h-9 w-20 tabular-nums', !(Number(yieldStr) > 0) && 'border-destructive')}
                      value={yieldStr}
                      onChange={(e) => setYieldStr(e.target.value)}
                      aria-invalid={!(Number(yieldStr) > 0)}
                    />
                    <span className="text-sm text-muted-foreground">serving{yieldN === 1 ? '' : 's'}</span>
                    <span className="text-xs text-muted-foreground">— type a whole cake or a tray and the number of slices; costs are per serving.</span>
                  </div>

                  <RecipeEditor
                    kind="product"
                    lines={lines}
                    onChange={setLines}
                    items={items}
                    itemList={itemList}
                    price={price}
                    yieldN={yieldN}
                    formatMoney={formatCurrency}
                    onCreateIngredient={(q, rowKey) => setCreateTarget({ where: 'base', rowKey, name: q })}
                    onSaveCost={saveCost}
                  />

                  {lines.length === 0 && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                      <span>Start from another item&apos;s recipe:</span>
                      {copyCandidates.filter((c) => c.id !== product.id).length === 0 ? (
                        <span>no other item has a recipe yet.</span>
                      ) : (
                        <Select value={startFrom} onValueChange={(v) => { setStartFrom(v); void copyFrom(v) }}>
                          <SelectTrigger className="h-8 w-64"><SelectValue placeholder="Copy from…" /></SelectTrigger>
                          <SelectContent>
                            {copyCandidates.filter((c) => c.id !== product.id).map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} · {c.count} ingredient{c.count === 1 ? '' : 's'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}

                  {(totals.loss || totals.uncosted > 0 || packagingOnly(totals.cost, product.projected_cost)) && (
                    <div className={cn('space-y-1 rounded-md px-3 py-2 text-xs', totals.loss ? 'bg-destructive/10 text-destructive' : 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300')}>
                      {totals.loss && <p className="font-medium">This recipe costs more than the item sells for. Every sale would book a {formatCurrency(totals.cost - price)} loss.</p>}
                      {totals.uncosted > 0 && (
                        <p>
                          {totals.uncosted} ingredient{totals.uncosted === 1 ? ' has' : 's have'} no cost yet, so the cost shown is too low. Use “Set cost” on the row.
                        </p>
                      )}
                      {totals.uncosted === 0 && packagingOnly(totals.cost, product.projected_cost) && (
                        <p>
                          The ingredients cost far less than your own figure ({formatCurrency(product.projected_cost ?? 0)}). Either an ingredient is missing, or your figure is out of date.
                        </p>
                      )}
                    </div>
                  )}

                  {serverBaseRows > 0 && hasEstimate && (
                    estimateInUse ? (
                      <p className="text-xs text-muted-foreground">
                        Your estimate of {product.projected_cost_basis === 'percent' ? `${product.projected_cost_percent ?? 0}% of price` : formatCurrency(estimateValue ?? 0)} is what checkout uses until every ingredient in the recipe has a cost.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        A manual estimate of{' '}
                        {product.projected_cost_basis === 'percent' ? `${product.projected_cost_percent ?? 0}% of price` : formatCurrency(estimateValue ?? 0)}{' '}
                        is also saved but not used while every ingredient has a cost.{' '}
                        <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => void removeDormant()}>
                          Remove it
                        </button>
                      </p>
                    )
                  )}

                  <VariantsSection
                    variants={recipe.variants}
                    drafts={variantDrafts}
                    onChange={(optionId, draft) => {
                      setVariantDrafts((prev) => {
                        const next = { ...prev }
                        if (draft) next[optionId] = draft
                        else delete next[optionId]
                        return next
                      })
                    }}
                    baseLines={lines}
                    yieldN={yieldN}
                    price={price}
                    items={items}
                    itemList={itemList}
                    formatMoney={formatCurrency}
                    onCreateIngredient={(optionId, q, rowKey) => setCreateTarget({ where: { optionId }, rowKey, name: q })}
                    onSaveCost={saveCost}
                  />
                </>
              )}

              {mode === 'amount' && (
                <div className="max-w-xs space-y-1.5">
                  <Label htmlFor="cost-amount">Cost per serving</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{currencyInputPrefix(currencyCode)}</span>
                    <Input id="cost-amount" type="number" min={0} step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 tabular-nums" autoFocus />
                  </div>
                </div>
              )}
              {mode === 'percent' && (
                <div className="max-w-xs space-y-1.5">
                  <Label htmlFor="cost-percent">Cost as % of price</Label>
                  <div className="flex items-center gap-2">
                    <Input id="cost-percent" type="number" min={0} max={100} step="0.01" inputMode="decimal" value={percent} onChange={(e) => setPercent(e.target.value)} className="h-9 tabular-nums" autoFocus />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">35 means the cost is 35% of the sale price.</p>
                </div>
              )}

              {/* Kitchen flow — the method the cooks read. Its own save; renders nothing without the flag. */}
              <RecipeMethodSection
                productId={product.id}
                guardRef={methodGuard}
                showToast={(t, m) => (t === 'success' ? toastHelpers.success(m) : toastHelpers.error(m))}
              />
            </>
          )}
        </SheetBody>

        <SheetFooter className="border-t">
          <p className={cn('flex-1 text-xs', reason && reason !== 'No changes to save' && reason !== 'Saving…' ? 'text-destructive' : 'text-muted-foreground')} aria-live="polite">
            {ready ? footerText : ''}
          </p>
          <Button type="button" variant="ghost" onClick={() => requestLeave(onClose)} disabled={saving}>Close</Button>
          <Button type="button" onClick={() => startSave()} disabled={!canSave}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {removesRecipe ? 'Save and remove recipe' : 'Save'}
          </Button>
        </SheetFooter>

        <AddStockItemModal
          open={createTarget != null}
          variant="admin"
          defaultName={createTarget?.name ?? ''}
          onClose={() => setCreateTarget(null)}
          onCreated={onIngredientCreated}
        />

        {/* Leaving with an unsaved kitchen recipe card — asked before the sheet's own edits. */}
        <Dialog open={methodLeave != null} onOpenChange={(o) => { if (!o) setMethodLeave(null) }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Unsaved recipe card for {product.name}</DialogTitle>
              <DialogDescription>
                {methodLeave?.canSave ? 'Save the method before you go, or discard it.' : 'The method has a problem and cannot be saved yet. Keep editing to fix it, or discard it.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setMethodLeave(null)}>Keep editing</Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  const run = methodLeave?.run
                  setMethodLeave(null)
                  run?.()
                }}
              >
                Discard changes
              </Button>
              {methodLeave?.canSave && (
                <Button
                  type="button"
                  onClick={async () => {
                    const run = methodLeave?.run
                    setMethodLeave(null)
                    if (await methodGuard.current?.save()) run?.()
                  }}
                >
                  {RC.save}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Leaving with unsaved edits (U-3): Prev, Next, Close, Esc, click-outside. */}
        <Dialog open={leave != null} onOpenChange={(o) => { if (!o) setLeave(null) }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Unsaved changes to {product.name}</DialogTitle>
              <DialogDescription>
                {canSave ? 'Save them before you go, or discard them.' : `They can't be saved yet: ${reason ?? ''}.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setLeave(null)}>Keep editing</Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  const run = leave?.run
                  setLeave(null)
                  run?.()
                }}
              >
                Discard changes
              </Button>
              {canSave && (
                <Button
                  type="button"
                  onClick={() => {
                    const run = leave?.run
                    setLeave(null)
                    startSave(run)
                  }}
                >
                  Save
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Shared option recipes (U-1): one confirm naming every option and its reach. */}
        <Dialog open={sharedConfirm != null} onOpenChange={(o) => { if (!o) setSharedConfirm(null) }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>These option recipes are shared</DialogTitle>
              <DialogDescription>Saving changes what every item carrying these options deducts, not just {product.name}.</DialogDescription>
            </DialogHeader>
            <ul className="space-y-1 text-sm">
              {sharedConfirm?.list.map((s) => (
                <li key={s.optionId}>
                  <span className="font-medium">{s.optionName}</span>{' '}
                  <span className="text-muted-foreground">
                    — {s.removes ? 'recipe removed from' : 'shared by'} {s.productCount} items that use {s.groupName}
                  </span>
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSharedConfirm(null)}>Keep editing</Button>
              <Button
                type="button"
                onClick={() => {
                  const after = sharedConfirm?.after
                  setSharedConfirm(null)
                  void runSave().then((ok) => {
                    if (ok && after) after()
                  })
                }}
              >
                Save for all
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  )
}
